import {
  memo,
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import slotMultiplierX2 from '../../assets/sounds/sc_slot_multiplier_x2.wav';
import slotMultiplierX3 from '../../assets/sounds/sc_slot_multiplier_x3.wav';
import slotMultiplierX5 from '../../assets/sounds/sc_slot_multiplier_x5.wav';
import slotReelSpin from '../../assets/sounds/sc_slot_reelSpin.wav';
import slotReelStart from '../../assets/sounds/sc_slot_reelStart.wav';
import slotReelStop1 from '../../assets/sounds/sc_slot_reelStop1.wav';
import slotReelStop2 from '../../assets/sounds/sc_slot_reelStop2.wav';
import slotReelStop3 from '../../assets/sounds/sc_slot_reelStop3.wav';
import { createSound, randomIntFromInterval, stopSounds } from '../../helpers';
import FriendsIcon from '../../icons/Friends';
import StoreIcon from '../../icons/Store';
import eventEmitter, { emitGameEvent, GameEvents } from '../../pages/Home/eventEmiter';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import Pages from '../../Router/pages';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  changeGame,
  getActiveSlotEffects,
  getSlotMachineSpinData, setSlotMachineGameStatus,
  setSlotMachineMultiplier,
} from '../../store/reducers/aviator/asyncActions';
import {
  updateProfileCoins,
  updateProfileEnergy,
  updateProfileRocketCoins,
  updateUserData,
} from '../../store/reducers/profile/asyncActions';
import {
  IAviatorStore, SlotMachineSpinResponse, TInGameStoreItem,
} from '../../types';
import ActiveBoosterCard from '../ActiveBoosterCard';
import AutopilotCard from '../AutopilotCard';
import Drawer from '../Drawer';
import AutopilotModal from './AutopilotModal';
import AviatorBoostersModal from './AviatorBoostersModal';
import Machine from './Machine/Machine';
import { MultiplierT } from './Machine/slotConstants';
import { getActiveChests } from '../../store/reducers/chests/asyncActions';
import AnimatedBackground from '../AnimatedBackground';
import useAppModalsStatus from '../../hooks/useAppModalsStatus';
import { useEnergyContext } from '../../providers/EnergyProvider';

type PurchasesModalType = 'autopilot' | 'aviatorBoosters'

const sounds = {
  spinStart: createSound(slotReelStart),
  multiplier2: createSound(slotMultiplierX2),
  multiplier3: createSound(slotMultiplierX3),
  multiplier5: createSound(slotMultiplierX5),
  reelSpinning: createSound(slotReelSpin),
  reelStop1: createSound(slotReelStop1),
  reelStop2: createSound(slotReelStop2),
  reelStop3: createSound(slotReelStop3),
};
function SlotMachineScreen() {
  const dispatch = useAppDispatch();
  const { webSocket } = useWebSocketContext();

  const { userEnergy, energyForSpin } = useEnergyContext();
  const reduxStateRef = useRef<SlotMachineSpinResponse | null>(null);
  const {
    slotMachineSpin, slotMachineType, slotMachineMultiplier: multiplier, slotGameStatus, activeGame,
  } = useAppSelector(
    ({ aviator }) => aviator,
  );
  const { purchases } = useAppSelector(
    ({ shop }) => shop,
  );

  reduxStateRef.current = slotMachineSpin;

  const isSpinning = slotGameStatus === 'spinning';

  const [purchasesModal, setPurchasesModal] = useState<{open: boolean; type?: PurchasesModalType}>({ open: false });

  const handleSpinChange = (status: IAviatorStore['slotGameStatus']) => {
    dispatch(setSlotMachineGameStatus(status));
  };

  let endGameTimeout: ReturnType<typeof setTimeout> | null = null;
  const spinTimeout: ReturnType<typeof setTimeout> | null = null;
  useEffect(() => () => {
    if (endGameTimeout) {
      clearTimeout(endGameTimeout);
    }
    if (spinTimeout) {
      clearTimeout(spinTimeout);
    }
    dispatch(setSlotMachineGameStatus('idle'));
  }, []);

  const endGame = async (spinData: SlotMachineSpinResponse) => {
    stopSounds(sounds);
    [sounds.reelStop1, sounds.reelStop2, sounds.reelStop3][randomIntFromInterval(0, 2)].play();
    const spinEffects = spinData?.effects;

    let connected = webSocket?.server?.readyState !== WebSocket.OPEN;

    if (spinEffects?.length) {
      const spinEffect = spinEffects[0];
      dispatch(updateUserData(spinData.user));
      switch (spinEffect.name) {
        case 'regenerate_energy':
          eventEmitter.emit(GameEvents.OVERLAY_SHOW_ENERGY_ANIMATION);
          eventEmitter.emit(GameEvents.SLOT_HIDE_MIDDLE);
          setTimeout(() => {
            handleSpinChange('idle');
          }, 1000);
          break;
        case 'bonus':
          dispatch(updateProfileCoins({ coins: spinData.user.coins, replace: true }));
          setTimeout(() => {
            handleSpinChange('idle');
          }, 1000);
          break;
        case 'rocket_coins_bonus':
          emitGameEvent({ event: GameEvents.SLOT_ROCKET_COINS_EXPLOSION });
          dispatch(updateProfileRocketCoins({ rocketCoins: spinData.user.rocket_coins, replace: true }));
          setTimeout(() => {
            handleSpinChange('idle');
          }, 1000);
          break;
        case 'launch_chest':
        case 'orbit_chest':
        case 'galaxy_chest':
        case 'supernova_chest':
          await dispatch(getActiveChests());
          setTimeout(() => {
            handleSpinChange('idle');
          }, 1200);
          break;
        case 'aviator':
          dispatch(changeGame('transition-stage'));

          if (!connected) {
            connected = await webSocket.connect();
          }

          if (connected) {
            eventEmitter.emit(GameEvents.OVERLAY_GALAXY_TRANSITION);
            endGameTimeout = setTimeout(() => {
              dispatch(changeGame('aviator'));
            }, 1000 * 1.4);
          }

          break;
        default:
          break;
      }
    }
    dispatch(getActiveSlotEffects());
  };
  const spinReels = async () => {
    Howler.stop();
    stopSounds(sounds);
    sounds.spinStart.play();
    sounds.spinStart.once('end', () => {
      if (!sounds.reelSpinning.playing()) {
        sounds.reelSpinning.loop(true);
        sounds.reelSpinning.play();
      }
    });
    if (userEnergy >= energyForSpin) {
      setIsAnimating(true);
      handleSpinChange('spinning');
      const resp = await dispatch(getSlotMachineSpinData({ multiplier }));

      if (resp?.payload?.slots) {
        dispatch(updateProfileEnergy({ energy: -(energyForSpin * multiplier), replace: false }));
        emitGameEvent({ event: GameEvents.SLOT_START_SPIN, targetSlots: (resp.payload as SlotMachineSpinResponse).slots.map((slot) => slot.type) });
      } else {
        setIsAnimating(false);
      }
    }
  };

  useEffect(() => {
    dispatch(getActiveSlotEffects());
    const handleStopEvent = () => {
      Howler.stop();
      stopSounds(sounds);
      if (reduxStateRef.current) {
        endGame(reduxStateRef.current);
      }
    };
    eventEmitter.on(GameEvents.SLOT_STOP_SPIN, handleStopEvent);
    return () => {
      eventEmitter.off(GameEvents.SLOT_STOP_SPIN, handleStopEvent);
    };
  }, []);
  useEffect(() => {
    const startAnimation = () => {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 50);
    };
    eventEmitter.on(GameEvents.ENERGY_HIT_SCREEN, startAnimation);
    return () => {
      eventEmitter.off(GameEvents.ENERGY_HIT_SCREEN, startAnimation);
    };
  }, []);

  const [isAnimating, setIsAnimating] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  }, [isAnimating]);

  const {
    dailyReward, chestHistory, leagues, tutorial, chestsHistoryLoading,
  } = useAppModalsStatus();

  useEffect(() => {
    if (slotMachineType === 'auto' && !isSpinning && !chestHistory && !chestsHistoryLoading && activeGame === 'slotMachine') {
      setTimeout(() => {
        spinReels();
      }, 2000);
    }
  }, [slotMachineType, isSpinning, activeGame, dailyReward, chestHistory, leagues, tutorial, chestsHistoryLoading]);

  const renderPurchase = useCallback((p: TInGameStoreItem) => {
    const isAutopilot = p.store_item_level.effects.findIndex((e) => e.name === 'autopilot') !== -1;

    if (p.is_active) {
      if (isAutopilot) {
        return <AutopilotCard key={p.id} purchase={p} onClick={() => setPurchasesModal({ open: true, type: 'autopilot' })} />;
      }

      return <ActiveBoosterCard key={p.id} purchase={p} onBoosterClick={() => setPurchasesModal({ open: true, type: 'aviatorBoosters' })} />;
    }

    return null;
  }, []);

  const temporaryBoosters = useMemo(() => {
    if (!purchases) {
      return [];
    }

    return purchases.filter((item) => item.is_active && item.store_item_level.store_item.type === 'temporary');
  }, [purchases]);

  const onPurchaseModalClose = useCallback(() => {
    setPurchasesModal((prev) => ({ open: false, type: prev.type }));
  }, []);

  const changeMultiplier = (val: MultiplierT) => {
    switch (val) {
      case 2:
        sounds.multiplier2.play();
        break;
      case 5:
        sounds.multiplier3.play();
        break;
      case 10:
        sounds.multiplier5.play();
        break;
      default:
        break;
    }
    dispatch(setSlotMachineMultiplier(val));
  };
  return (
    <div
      className="absolute top-0 w-screen h-screen flex flex-col items-center justify-end select-none"
    >
      <AnimatedBackground backgroundPage="upgradePage" />
      <div className="w-full h-fit fixed bottom-5">
        <div
          style={{
            position: 'relative',
            transform: 'none',
          }}
        >
          <Machine
            onSpin={() => {
              spinReels();
            }}
            isSpinning={isSpinning}
            multiplier={multiplier}
            setMultiplier={changeMultiplier}
          />
        </div>
      </div>
      <SlotPageNavigation />
      <div className="absolute top-[5.1rem] right-[0.7rem] p-1 rounded flex flex-col gap-[24px] max-h-[58vh] overflow-y-scroll">
        {purchases?.map((purchase) => renderPurchase(purchase))}
      </div>
      <Drawer
        side="bottom"
        open={purchasesModal.open}
        onClose={onPurchaseModalClose}
        closeStroke="gray-10"
        wrapperClassName="flex flex-col overflow-y-scroll h-fit shadow-xl rounded-t-[20px] before:rounded-t-[20px] bg-gradient-to-b from-[#4344C1] to-[#2D2EA0] z-[9] relative stroke-gray-10"
      >
        {purchasesModal.type === 'autopilot' && (
        <AutopilotModal
          onClose={onPurchaseModalClose}
        />
        )}
        {purchasesModal.type === 'aviatorBoosters' && (
          <AviatorBoostersModal
            items={temporaryBoosters}
            onClose={onPurchaseModalClose}
          />
        )}
      </Drawer>
    </div>
  );
}

export default SlotMachineScreen;

const SlotPageNavigation = memo(() => (
  <div
    className="
        absolute
        top-[5.1rem] left-[1rem]
        flex flex-col gap-2
        "
  >
    <Link
      to={Pages.boosters}
      className="
          slot-nav-link
          gradient-border primary before:z-[1]
          "
    >
      <StoreIcon />
      <span className="block mt-[-16px] font-Bangers text-xs">Boosters</span>
    </Link>
    <Link
      to={Pages.friends}
      className="
          slot-nav-link
          gradient-border primary before:z-[1]
          "
    >
      <FriendsIcon />
      <span className="block mt-[-21px] font-Bangers text-xs">Friends</span>
    </Link>
  </div>
));
SlotPageNavigation.displayName = 'SlotPageNavigation';
